import axios from "axios";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requestPasswordReset, resetPassword } from "../../services/AuthService";
import { AppRoutes } from "../../types/constants.types";

const PASSWORD_PATTERN = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

const fieldClassName = "w-full rounded-[18px] border border-slate-200/90 bg-white/95 px-4 py-3.5 text-slate-900 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_10px_30px_rgba(15,23,42,0.03)] transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 dark:border-white/10 dark:bg-slate-950/76 dark:text-slate-100 dark:placeholder:text-slate-500";

const getErrorMessage = (error: unknown) => {
	if (axios.isAxiosError(error) && error.response?.status === 401) {
		return "That verification code is invalid or has expired. Request a new code and try again.";
	}

	return "We could not complete the password reset. Please try again.";
};

export default function ForgotPasswordForm() {
	const navigate = useNavigate();
	const [step, setStep] = useState<"request" | "reset">("request");
	const [email, setEmail] = useState("");
	const [token, setToken] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");

	const sendCode = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setLoading(true);
		setError("");

		try {
			await requestPasswordReset(email.trim());
			setEmail(email.trim());
			setNotice("If an E2G account exists for this email, a verification code has been sent. Check your inbox and spam folder.");
			setStep("reset");
		} catch (requestError) {
			setError(getErrorMessage(requestError));
		} finally {
			setLoading(false);
		}
	};

	const submitNewPassword = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError("");

		if (!PASSWORD_PATTERN.test(password)) {
			setError("Use at least 8 characters with an uppercase letter, a number, and a special character (!@#$%^&*).");
			return;
		}

		if (password !== confirmPassword) {
			setError("The passwords do not match.");
			return;
		}

		setLoading(true);
		try {
			await resetPassword({ email, token: token.trim(), newpassword: password });
			navigate(AppRoutes.LOGIN, {
				replace: true,
				state: { passwordReset: true },
			});
		} catch (resetError) {
			setError(getErrorMessage(resetError));
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="w-full max-w-[490px]">
			<div className="relative overflow-hidden rounded-[30px] border border-white/70 bg-white/90 p-7 shadow-[0_30px_90px_rgba(2,8,23,0.14)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/78 sm:p-9">
				<div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent" />
				<div className="relative z-10 mb-7">
					<p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-300">E2G Admin Portal</p>
					<h1 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">Reset your password</h1>
					<p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
						{step === "request"
							? "Use the email address linked to your E2G platform account."
							: "Enter the verification code from your E2G Platform email and choose a new password."}
					</p>
				</div>

				{notice && <div className="relative z-10 mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200" role="status">{notice}</div>}
				{error && <div className="relative z-10 mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-400/30 dark:bg-red-900/20 dark:text-red-200" role="alert">{error}</div>}

				{step === "request" ? (
					<form className="relative z-10 space-y-5" onSubmit={sendCode}>
						<div className="space-y-2">
							<label htmlFor="reset-email" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Email address</label>
							<input id="reset-email" type="email" autoComplete="email" autoFocus required value={email} onChange={(event) => setEmail(event.target.value)} className={fieldClassName} placeholder="Enter your email address" />
						</div>
						<button type="submit" disabled={loading || !email.trim()} className="w-full rounded-[18px] bg-emerald-600 px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-[0_20px_38px_rgba(16,185,129,0.3)] transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
							{loading ? "Sending..." : "Send verification code"}
						</button>
					</form>
				) : (
					<form className="relative z-10 space-y-4" onSubmit={submitNewPassword}>
						<div className="space-y-2">
							<label htmlFor="reset-code" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Verification code</label>
							<input id="reset-code" type="text" inputMode="numeric" autoComplete="one-time-code" autoFocus required value={token} onChange={(event) => setToken(event.target.value.replace(/\D/g, "").slice(0, 6))} className={fieldClassName} placeholder="6-digit code" />
						</div>
						<div className="space-y-2">
							<label htmlFor="new-password" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">New password</label>
							<input id="new-password" type="password" autoComplete="new-password" required value={password} onChange={(event) => setPassword(event.target.value)} className={fieldClassName} placeholder="Enter a new password" />
						</div>
						<div className="space-y-2">
							<label htmlFor="confirm-password" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Confirm password</label>
							<input id="confirm-password" type="password" autoComplete="new-password" required value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} className={fieldClassName} placeholder="Re-enter your new password" />
						</div>
						<p className="text-xs leading-5 text-slate-500 dark:text-slate-400">At least 8 characters, including an uppercase letter, a number, and one of !@#$%^&amp;*.</p>
						<button type="submit" disabled={loading || token.length !== 6 || !password || !confirmPassword} className="w-full rounded-[18px] bg-emerald-600 px-4 py-3.5 text-sm font-semibold uppercase tracking-[0.2em] text-white shadow-[0_20px_38px_rgba(16,185,129,0.3)] transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
							{loading ? "Resetting..." : "Reset password"}
						</button>
						<button type="button" disabled={loading} onClick={() => { setStep("request"); setToken(""); setNotice(""); setError(""); }} className="w-full text-sm font-medium text-emerald-700 hover:text-emerald-600 disabled:opacity-50 dark:text-emerald-300">Request a new code</button>
					</form>
				)}

				<div className="relative z-10 mt-6 border-t border-slate-200 pt-5 text-center text-sm dark:border-white/10">
					<Link to={AppRoutes.LOGIN} className="font-medium text-emerald-700 hover:text-emerald-600 dark:text-emerald-300">Back to sign in</Link>
				</div>
			</div>
		</div>
	);
}